import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { jsPDF } from 'jspdf';
import { useCompany, useContacts, useMessages } from '../Store/storeFunctions';
import { toastPromise } from '../Helpers/ToastMessage';
import { initializeApp } from '@firebase/app';
import { getFirestore, getDoc, doc } from '@firebase/firestore';
import EmojiPicker, { Emoji } from 'emoji-picker-react';
import { getFunctions, httpsCallable } from "firebase/functions";
import * as ContactTools from './Messaging/Contacts';
import TiptapEmailTemplate from '../Form/BuilderTools/TipTapEmailTemplate';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { FormikTextInput } from '../FormikFields';

const app = initializeApp({
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
});
const db = getFirestore(app);
const functions = getFunctions();
const updateSubmissionTags = httpsCallable(functions, "updateSubmissionTags");
const sendSMS = httpsCallable(functions, "sendSMS");
const markMessageUnread = httpsCallable(functions, "markMessageUnread");
const markMessagesRead = httpsCallable(functions, "markMessagesRead");
const sendBulkEmail = httpsCallable(functions, "sendBulkEmail");

function SubmissionDetails(props) {
  const userCompany = useCompany();
  const contacts = useContacts();
  const messageStore = useMessages();
  const [tags, setTags] = React.useState([]);
  const [addTagIndex, setAddTagIndex] = React.useState(-1);
  
  // Messaging related state
  const [messages, setMessages] = React.useState([]);
  const [newMessage, setNewMessage] = React.useState("");
  const [messagingEnabled, setMessagingEnabled] = React.useState(false);
  const [contactPhone, setContactPhone] = React.useState("");
  
  // Email related state
  const [emailTemplate, setEmailTemplate] = React.useState("<p></p>");
  const [emailConfig, setEmailConfig] = React.useState({ subject: '' });
  const [emailEnabled, setEmailEnabled] = React.useState(false);
  const [contactEmail, setContactEmail] = React.useState("");
  const [seed, setSeed] = React.useState(0);

  React.useEffect(() => {
    if (userCompany.companyData?.extraFeatures?.submissionTagging) {
      fetchTags();
    }
    
    // Check if messaging is enabled and contact has a phone number
    const phoneNumber = getPhoneFromAnswers();
    if (phoneNumber !== "-N/A-") {
      setContactPhone(phoneNumber);
      if (userCompany.companyData.twilio?.sid && 
          userCompany.companyData.twilio?.phoneNumber && 
          userCompany.companyData.twilio?.verified) {
        setMessagingEnabled(true);
      }
    }
    
    // Check if email is available
    const email = getEmailFromAnswers();
    if (email !== "-N/A-") {
      setContactEmail(email);
      setEmailEnabled(true);
    }
  }, []);

  function fetchTags() {
    toastPromise(async () => {
        var tagDoc = await getDoc(doc(db, `/BusinessRules/${userCompany.companyData.code}/Tags/0`));

        if (tagDoc && tagDoc.exists()) {
          var data = tagDoc.data();
    
          if (data && data.Tags) {
            setTags(data.Tags);
          }
        }
      },
      true
    )
  }

  function downloadDetailPDF() {
    const doc = new jsPDF('portrait', 'px');

    doc.setFontSize(18);

    const html = printableHtml();
    const htmlString = ReactDOMServer.renderToStaticMarkup(html);

    doc.addImage('/assets/logo-1.png', 'PNG', 75, 10, 300, 50);

    doc.html(htmlString, {
      callback: function (doc) {
        doc.save(`${getNameFromAnswers()}-${new Date().toISOString()}-submission.pdf`);
      },
      margin: [0, 10, 0, 10],
      html2canvas: {
        scale: 0.5,
        autoPaging: 'text',
        scrollX: 0,
        scrollY: 0,
        windowWidth: 750,
        windowHeight: 1000
      },
      y: 75
    });
  }

  function printableHtml() {
    return (
      <div className="container" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="row" style={{ width: 750, marginBottom: 10 }}>
          <h1>{getNameFromAnswers()}</h1>
          <h5>{getEmailFromAnswers()}</h5>
        </div>

        <div style={{ marginTop: 25, width: 750 }}>
          <h4>{props.Submission.FormTitle}</h4>
        </div>

        <div className="row" style={{ width: 750, pageBreakAfter: 'always', marginTop: 25 }}>
          <h4>Required Documents</h4>
          <div style={{ marginLeft: 15 }}>{getDocs()}</div>
        </div>

        <div className="row" style={{ width: 750, pageBreakBefore: 'always', marginTop: 25 }}>
          <h4>Questions and Answers</h4>

          <div style={{ marginLeft: 15 }}>{getQuestionsAndAnswers()}</div>
        </div>
      </div>
    );
  }

  function getBusinessLegalFromAnswers() {
    const list = props.Submission.Questions.filter(q => q.Text == "Business Legal name");

    if (list && list.length == 1)
      return list[0].Answer;
    else
      return "-N/A-";
  }

  function getNameFromAnswers() {
    const businessName = getBusinessLegalFromAnswers();

    if (businessName && businessName !== "-N/A-")
      return businessName;
    
    const list = props.Submission.Questions.filter(q => q.Text == "First Name" || q.Text == "Last Name");

    if (list && list.length == 2) {
      var str = " ";

      list.forEach(element => {
        if (element.Text == "First Name") {
          str = element.Answer + str;
        } else {
          str = str + element.Answer;
        }
      });

      return str;
    } else {
      const fullName = props.Submission.Questions.filter(q => q.Text == "Name of Submitter");

      if (fullName && fullName.length == 1) {
        return fullName[0].Answer;
      } else {
        return "-N/A-";
      }
    }
  }

  function getEmailFromAnswers() {
    const list = props.Submission.Questions.filter(q => q.Text == "Email");

    if (list && list.length == 1)
      return list[0].Answer;
    else
      return "-N/A-";
  }
  
  function getPhoneFromAnswers() {
    const list = props.Submission.Questions.filter(q => q.Text == "Phone" || q.Text == "Phone Number" || q.Text == "Mobile" || q.Text == "Mobile Number");

    if (list && list.length >= 1)
      return list[0].Answer;
    else
      return "-N/A-";
  }

  function getQuestionsAndAnswers() {
    var answered = props.Submission.Questions.filter(q => q.Answer);

    return answered.map((q, i) => {
      return <>
        <b>{q.Text}</b>
        <p style={{ marginBottom: 0 }}>{q.Answer.toString()}</p>

        { !userCompany.companyData?.extraFeatures?.submissionTagging ? null :
          <div style={{ display: 'flex' }}>
            {renderTags(i, "Q")}

            { addTagIndex == i ?
              <button className='btn btn-light btn-sm' style={{ marginRight: 4 }} title='Hide tagging options' onClick={() => setAddTagIndex(-1)}><span className='bi-tag' ></span></button>
              : <button className='btn btn-light btn-sm' style={{ marginRight: 4 }} title='Show tagging options' onClick={() => setAddTagIndex(i)}><span className='bi-tag-fill' ></span></button>
            }
            
            {renderEmojiPicker(i, "Q")}
          </div>
        }

        { i < answered.length - 1 ? <hr style={{ marginTop: 8, marginBottom: 8 }} /> : null }
      </>
    })
  }

  function updateTags(tag, index, sourceType) {
    var existing = -1;
    var docTags = props.Submission.DocumentTags ?? [];
    var quesTags = props.Submission.QuestionTags ?? [];

    if (sourceType == "D") {
      existing = docTags.findIndex(d => d.Source == props.Submission.Documents[index] && d.Unified == tag);

      if (existing == -1) {
        docTags.push({ Source: props.Submission.Documents[index], Unified: tag });
      } else {
        docTags.splice(existing, 1);
      }
    } else {
      existing = quesTags.findIndex(q => q.Source == props.Submission.Questions[index].Text && q.Unified == tag);

      if (existing == -1) {
        quesTags.push({ Source: props.Submission.Questions[index].Text, Unified: tag });
      } else {
        quesTags.splice(existing, 1);
      }
    }

    var data = {
      CompanyID: userCompany.companyData.code,
      SubmissionID: props.Submission.ID,
      DocumentTags: docTags,
      QuestionTags: quesTags
    }

    toastPromise(async () => {
      var res = await updateSubmissionTags(data);

      if (res) {
        // success so update state.
        props.updateSubmission(data)
      }
    }, true);
  }

  function renderTags(index, sourceType) {
    var tagsToShow = [];

    if (sourceType == "D") {
      if (!props.Submission.DocumentTags) return null;

      tagsToShow = props.Submission.DocumentTags.filter(d => d.Source == props.Submission.Documents[index]);
    } else {
      if (!props.Submission.QuestionTags) return null;

      tagsToShow = props.Submission.QuestionTags.filter(q => q.Source == props.Submission.Questions[index].Text);
    }

    if (tagsToShow.length == 0) return null;

    return tagsToShow.map((t, i) => {
      var tag = tags.find(tag => tag.Icon == t.Unified);

      return <div key={i} style={{ display: 'flex', alignItems: 'center', marginRight: 4 }} title={tag?.Description}>
        <Emoji unified={t.Unified} />
      </div>
    })
  }

  function renderEmojiPicker(index, sourceType) {
    if (userCompany.companyData?.extraFeatures?.submissionTagging && addTagIndex == index) {
      var reactions = tags.map(t => t.Icon);

      return (
        <EmojiPicker reactions={reactions}
          onReactionClick={(e) => updateTags(e.unified, index, sourceType)}
          allowExpandReactions={false}
          reactionsDefaultOpen={true}
        />
      )
    } else {
      return null;
    }
  }

  function getDocs() {
    if (!props.Submission.Documents || props.Submission.Documents.length === 0) {
      return <p>None</p>
    }
  
    return props.Submission.Documents.map((doc, i) => {
      const isReceived = props.Submission.ReceivedDocuments && 
                        props.Submission.ReceivedDocuments.includes(doc);
  
      return (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input 
              type="checkbox"
              style={{ cursor: 'pointer' }}
              checked={isReceived}
              onChange={() => handleDocumentCheck(doc, !isReceived)}
              title={isReceived ? "Mark as not received" : "Mark as received"}
              />
            
            <p style={{ marginBottom: 0 }} dangerouslySetInnerHTML={{ __html: doc }}></p>
          </div>

          {
            !userCompany.companyData?.extraFeatures?.submissionTagging ? null :
            <div style={{ display: 'flex' }}>
              {renderTags(i, "D")}

              { addTagIndex == i ?
                <button className='btn btn-light btn-sm' style={{ marginRight: 4 }} title='Hide tagging options' onClick={() => setAddTagIndex(-1)}><span className='bi-tag' ></span></button>
                : <button className='btn btn-light btn-sm' style={{ marginRight: 4 }} title='Show tagging options' onClick={() => setAddTagIndex(i)}><span className='bi-tag-fill' ></span></button>
              }
              
              {renderEmojiPicker(i, "D")}
            </div>
          }

          {i < props.Submission.Documents.length - 1 ? 
            <hr style={{ marginTop: 8, marginBottom: 8 }} /> : 
            null
          }
        </div>
      )
    })
  }
  
  function handleDocumentCheck(document, isChecked) {
    if (props.onDocumentReceived) {
      props.onDocumentReceived(document, isChecked);
    }
  }
  
  // Messaging related functions
  React.useEffect(() => {
    if (messageStore.messages?.Messages && userCompany.companyData.twilio?.phoneNumber && contactPhone) {
      const messageContainer = messageStore.messages;
      
      // Filter messages for this contact's phone number
      const contactMessages = messageContainer.Messages.flat()
        .filter(m => {
          const normalizedContactPhone = contactPhone.replace(/\D/g,'');
          const normalizedFrom = m.From.replace(/\D/g,'');
          const normalizedTo = m.To.replace(/\D/g,'');
          
          return normalizedFrom.endsWith(normalizedContactPhone) || 
                 normalizedTo.endsWith(normalizedContactPhone);
        })
        .sort((a, b) => new Date(a.DateTime) - new Date(b.DateTime));
      
      setMessages(contactMessages);
    }
  }, [messageStore.messages, contactPhone, userCompany.companyData.twilio?.phoneNumber]);
  
  // Scroll to bottom of conversation history when messages change
  React.useEffect(() => {
    setTimeout(() => {
      const element = document.getElementById("conversationHistory");
      if (element) {
        element.scrollTo({
          top: element.scrollHeight,
          left: 0,
          behavior: "smooth",
        });
      }
    }, 100);
  }, [messages]);
  
  function sendMessage() {
    if (!newMessage.trim() || !contactPhone || !messagingEnabled) {
      return;
    }
    
    const messageText = newMessage;
    setNewMessage("");
    
    toastPromise(async () => {
      await sendSMS({
        from: userCompany.companyData.twilio.phoneNumber,
        to: contactPhone,
        message: messageText,
        company: userCompany.companyData.code
      });
    }, false, "Sending Text Message...", "Sent!", "Failed to send.");
  }
  
  function renderConversationHistory() {
    if (!messagingEnabled || contactPhone === "-N/A-") {
      return (
        <div className="alert alert-warning">
          <p>Messaging is not available. This requires:</p>
          <ul>
            <li>Contact must have a phone number</li>
            <li>Company must have Twilio configured</li>
            <li>Twilio must be verified and active</li>
          </ul>
        </div>
      );
    }
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div id="conversationHistory" style={{
          height: "calc(100% - 80px)",
          overflowY: 'scroll',
          padding: 12,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {messages.length === 0 ? (
            <p className="text-center text-muted">No message history with this contact.</p>
          ) : (
            messages.map((m, index) => (
              <div key={index} style={{ margin: 5 }}>
                <p style={{
                  backgroundColor: m.From === userCompany.companyData.twilio?.phoneNumber ? "whitesmoke" : "var(--bs-light-blue)",
                  maxWidth: 300,
                  float: m.From === userCompany.companyData.twilio?.phoneNumber ? 'right' : 'left',
                  padding: 8,
                  borderRadius: 8,
                  wordWrap: 'break-word'
                }}>
                  {m.Body}
                  <br />
                  <small style={{ color: 'rgba(0,0,0,0.4)', fontSize: 12 }}>
                    <i>{new Date(m.DateTime).toLocaleString()}</i>
                  </small>
                </p>
              </div>
            ))
          )}
        </div>
        
        <div className="input-group p-2" style={{ zIndex: 0 }}>
          <textarea 
            className="form-control form-control-sm" 
            placeholder='Text Message - SMS' 
            value={newMessage} 
            onChange={(e) => setNewMessage(e.target.value)} 
            style={{ width: "calc(100% - 60px)" }} 
            aria-describedby='btnSend' 
          />
          <button 
            className="form-control btn btn-sm btn-primary" 
            id='btnSend' 
            type="button" 
            onClick={sendMessage}
          >
            <span className="bi-send"></span>
          </button>
        </div>
      </div>
    );
  }

  // Email related functions
  const EmailSchema = Yup.object().shape({
    subject: Yup.string().required('Required')
  });
  
  function sendEmail(values, formikBag) {
    if (!emailEnabled || contactEmail === "-N/A-") {
      return;
    }
    
    toastPromise(async () => {
      // Create a contact object with the submission data
      const contact = {
        ID: props.Submission.ID,
        FirstName: getFirstNameFromAnswers(),
        LastName: getLastNameFromAnswers(),
        Email: contactEmail,
        PhoneNumber: contactPhone
      };
      
      const sendData = {
        companyId: userCompany.companyData.code,
        contacts: [contact],
        subject: values.subject,
        toEmailField: "Email",
        messageBody: emailTemplate,
        interpolations: interpolationList()
      };
      
      await sendBulkEmail(sendData);
      
      // Reset form
      formikBag.resetForm();
      setEmailConfig({ subject: '' });
      setEmailTemplate("<p></p>");
      setSeed(seed + 1);
    }, false, "Sending Email...", "Email Sent!", "Failed to send email.");
  }
  
  function getFirstNameFromAnswers() {
    const list = props.Submission.Questions.filter(q => q.Text == "First Name");
    if (list && list.length == 1)
      return list[0].Answer;
    else
      return "";
  }
  
  function getLastNameFromAnswers() {
    const list = props.Submission.Questions.filter(q => q.Text == "Last Name");
    if (list && list.length == 1)
      return list[0].Answer;
    else
      return "";
  }
  
  const interpolationList = React.useCallback((options) => {
    const query = options?.query;
    
    const interpolations = [
      { ID: "CompanyName", Label: "Company Data - Name" },
      { ID: "CompanyEmail", Label: "Company Data - Email" },
      { ID: "CompanyWebsite", Label: "Company Data - Website" },
      { ID: "CompanyPortal", Label: "Company Data - Portal" },
      { ID: "CompanyPhone", Label: "Company Data - Phone" },
      { ID: "CompanyAddress", Label: "Company Data - Address" },
      { ID: "IndividualFormLink", Label: "Individual - Form Link" },
      { ID: "BusinessFormLink", Label: "Business - Form Link" },
      { ID: "TrustFormLink", Label: "Trust - Form Link" },
      { ID: "FirstName", Label: "Contact - First Name" },
      { ID: "LastName", Label: "Contact - Last Name" },
      { ID: "Email", Label: "Contact - Email" },
      { ID: "PhoneNumber", Label: "Contact - Phone Number" }
    ];
    
    if (query) {
      return interpolations.filter(i => i.Label.toLowerCase().startsWith(query.toLowerCase()));
    }
    
    return interpolations;
  }, []);
  
  function showSendEmail() {
    return emailEnabled && contactEmail !== "-N/A-" && emailTemplate.length > 0 && emailTemplate !== "<p></p>";
  }
  
  function renderEmailForm() {
    if (!emailEnabled || contactEmail === "-N/A-") {
      return (
        <div className="alert alert-warning">
          <p>Email is not available. This requires:</p>
          <ul>
            <li>Contact must have an email address</li>
          </ul>
        </div>
      );
    }
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Formik
          initialValues={emailConfig}
          validationSchema={EmailSchema}
          onSubmit={(values, formikBag) => sendEmail(values, formikBag)}
        >
          {({ errors, touched }) => (
            <Form>
              <Field errors={errors} name="subject" component={FormikTextInput} formiklabel="Subject" />
              
              <div className="mb-3">
                <label className="form-label">Email Content</label>
                <TiptapEmailTemplate 
                  itemResource={interpolationList} 
                  seed={seed} 
                  value={emailTemplate} 
                  setValue={(v) => setEmailTemplate(v)} 
                />
              </div>
              
              <div className="mb-3 mt-3 text-center">
                <button 
                  className="btn btn-sm btn-primary" 
                  type="submit" 
                  disabled={!showSendEmail()}
                >
                  Send Email
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
  
  function getLastUpdateTime() {
    if (props.Submission.LastStatusUpdate) {
      return props.Submission.LastStatusUpdate.toDate().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric"
      });
    } else {
      return props.Submission.Timestamp.toDate().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric"
      });
    }
  }

  function getStatus() {
    if (!props.usesStatus) {
      return null;
    }
    
    var statusColor = {};

    if (props.Submission.Status == "Complete") {
      statusColor.color = "var(--green)";
    } else {
      statusColor.color = "var(--yellow)";
    }

    return <span><span style={{ ...statusColor }}>{props.Submission.Status}</span> - </span>;
  }

  function render() {
    return (
      <div className="container" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="row" style={{ height: 200 }}>
          <h1>
            {getNameFromAnswers()} - {getEmailFromAnswers()}
            <span title="Download Submission Details" style={{ fontSize: 25, float: "right", cursor: 'pointer' }} className="bi-download" onClick={() => {
              downloadDetailPDF();
            }}></span>
          </h1>
          <h5>{props.Submission.FormTitle}</h5>
          <h4>{getStatus()}{getLastUpdateTime()}</h4>
        </div>


        <div className="row" style={{ height: 'calc(100% - 200px)' }}>
          <nav>
            <div className="nav nav-tabs" id="nav-tab" role="tablist">
              <button className="nav-link active" id="nav-qa-tab" data-bs-toggle="tab" data-bs-target="#nav-qa" type="button" role="tab" aria-controls="nav-qa" aria-selected="true">Answers</button>
              <button className="nav-link" id="nav-docs-tab" data-bs-toggle="tab" data-bs-target="#nav-docs" type="button" role="tab" aria-controls="nav-docs" aria-selected="false">Required Documents</button>
              <button className="nav-link" id="nav-messages-tab" data-bs-toggle="tab" data-bs-target="#nav-messages" type="button" role="tab" aria-controls="nav-messages" aria-selected="false">
                Messages
                {contactPhone !== "-N/A-" && messagingEnabled ? null : <i className="bi bi-exclamation-triangle-fill text-warning ms-2"></i>}
              </button>
              <button className="nav-link" id="nav-email-tab" data-bs-toggle="tab" data-bs-target="#nav-email" type="button" role="tab" aria-controls="nav-email" aria-selected="false">
                Send Email
                {contactEmail !== "-N/A-" && emailEnabled ? null : <i className="bi bi-exclamation-triangle-fill text-warning ms-2"></i>}
              </button>
            </div>
          </nav>

          <div className="tab-content" id="nav-tabContent" style={{ padding: 20, paddingBottom: 0, overflowY: 'scroll', height: 'calc(100% - 50px)' }}>
            <div className="tab-pane fade show active" id="nav-qa" role="tabpanel" aria-labelledby="nav-qa-tab">{getQuestionsAndAnswers()}</div>
            <div className="tab-pane fade" id="nav-docs" role="tabpanel" aria-labelledby="nav-docs-tab">{getDocs()}</div>
            <div className="tab-pane fade" id="nav-messages" role="tabpanel" aria-labelledby="nav-messages-tab" style={{ height: '100%' }}>{renderConversationHistory()}</div>
            <div className="tab-pane fade" id="nav-email" role="tabpanel" aria-labelledby="nav-email-tab" style={{ height: '100%' }}>{renderEmailForm()}</div>
          </div>
        </div>
      </div>
    );
  }

  return render();
}

const styles = {
}

export default SubmissionDetails;